import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { loadBpmnJs, loadBpmnStyles } from './bpmn-js-loader';

export interface WorkflowBpmnElementSelection {
  id: string;
  name: string;
  type: string;
}

@Component({
  standalone: true,
  selector: 'ain-workflow-bpmn-diagram',
  template: `
    <div class="diagram-shell">
      <div #canvas class="diagram-canvas"></div>
    </div>
  `,
  styles: [`
    .diagram-shell {
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      overflow: hidden;
      background: #f8fafc;
      min-height: 360px;
    }
    .diagram-canvas { height: 420px; }
    :host ::ng-deep .djs-palette,
    :host ::ng-deep .djs-context-pad,
    :host ::ng-deep .djs-popup,
    :host ::ng-deep .djs-overlay-container .djs-overlay-search {
      display: none !important;
    }
    :host ::ng-deep .highlight-current .djs-visual > :nth-child(1) {
      stroke: #6d28d9 !important;
      stroke-width: 4px !important;
      fill: #ede9fe !important;
    }
    :host ::ng-deep .highlight-completed .djs-visual > :nth-child(1) {
      stroke: #047857 !important;
      stroke-width: 3px !important;
      fill: #d1fae5 !important;
    }
    :host ::ng-deep .highlight-upcoming .djs-visual > :nth-child(1) {
      stroke: #94a3b8 !important;
      stroke-dasharray: 6 4;
    }
    :host ::ng-deep .highlight-selected .djs-visual > :nth-child(1) {
      stroke: #2563eb !important;
      stroke-width: 4px !important;
    }
  `]
})
export class WorkflowBpmnDiagramComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('canvas', { static: true }) private readonly canvas?: ElementRef<HTMLDivElement>;

  @Input() xml = '';
  @Input() currentElementIds: string[] = [];
  @Input() completedElementIds: string[] = [];
  @Input() upcomingElementIds: string[] = [];
  @Input() selectedElementId: string | null = null;
  @Output() readonly elementSelected = new EventEmitter<WorkflowBpmnElementSelection>();

  private viewer: InstanceType<NonNullable<Window['BpmnJS']>> | null = null;
  private importedXml = '';
  private markedIds = new Set<string>();
  private clickBound = false;
  private destroyed = false;
  private renderChain = Promise.resolve();

  async ngAfterViewInit(): Promise<void> {
    await this.ensureViewer();
    if (this.destroyed) {
      this.disposeViewer();
      return;
    }

    await this.render();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (this.destroyed || !this.viewer) {
      return;
    }

    if (changes['xml']) {
      await this.render();
      return;
    }

    this.applyMarkers();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.disposeViewer();
  }

  private async ensureViewer(): Promise<void> {
    if (this.destroyed || this.viewer || !this.canvas) {
      return;
    }

    await loadBpmnJs();
    if (this.destroyed) {
      return;
    }

    await loadBpmnStyles();
    if (this.destroyed || !this.canvas) {
      return;
    }

    if (!window.BpmnJS) {
      throw new Error('bpmn_js_not_loaded');
    }

    this.viewer = new window.BpmnJS({ container: this.canvas.nativeElement });
    this.bindClicks();
  }

  private disposeViewer(): void {
    this.viewer?.destroy();
    this.viewer = null;
    this.clickBound = false;
    this.importedXml = '';
    this.markedIds.clear();
  }

  private render(): Promise<void> {
    this.renderChain = this.renderChain.then(() => this.renderLatest(), () => this.renderLatest());
    return this.renderChain;
  }

  private async renderLatest(): Promise<void> {
    if (this.destroyed || !this.viewer) {
      return;
    }

    const xml = this.xml;
    if (!xml.trim()) {
      return;
    }

    if (xml === this.importedXml) {
      this.applyMarkers();
      return;
    }

    try {
      await this.viewer.importXML(xml);
    } catch {
      if (this.destroyed || this.xml !== xml) {
        return;
      }

      this.importedXml = '';
      this.markedIds.clear();
      try {
        this.viewer.clear();
      } catch {
        // keep the runtime desk usable even if the previous diagram cannot be cleared
      }
      return;
    }

    if (this.destroyed) {
      this.disposeViewer();
      return;
    }

    if (this.xml !== xml) {
      return;
    }

    this.importedXml = xml;
    this.fit();
    this.applyMarkers();
  }

  private bindClicks(): void {
    if (!this.viewer || this.clickBound) {
      return;
    }

    this.clickBound = true;
    const eventBus = this.viewer.get('eventBus');
    eventBus?.on?.('element.click', (event: { element?: { id?: string; type?: string; businessObject?: { id?: string; name?: string; $type?: string } } }) => {
      const element = event.element;
      if (!element?.id || element.type === 'label' || element.type === 'bpmn:Process' || element.type === 'bpmn:Collaboration') {
        return;
      }

      const business = element.businessObject;
      this.elementSelected.emit({
        id: business?.id || element.id,
        name: business?.name || '',
        type: business?.$type || element.type || ''
      });
    });
  }

  private applyMarkers(): void {
    if (this.destroyed || !this.viewer) {
      return;
    }

    const canvas = this.viewer.get('canvas');
    for (const id of this.markedIds) {
      canvas?.removeMarker?.(id, 'highlight-current');
      canvas?.removeMarker?.(id, 'highlight-completed');
      canvas?.removeMarker?.(id, 'highlight-upcoming');
      canvas?.removeMarker?.(id, 'highlight-selected');
    }

    this.markedIds.clear();
    const add = (ids: string[], css: string) => {
      for (const id of ids) {
        if (!id) {
          continue;
        }
        try {
          canvas?.addMarker?.(id, css);
          this.markedIds.add(id);
        } catch {
          // element may not exist in this XML
        }
      }
    };

    add(this.completedElementIds, 'highlight-completed');
    add(this.upcomingElementIds, 'highlight-upcoming');
    add(this.currentElementIds, 'highlight-current');
    if (this.selectedElementId) {
      add([this.selectedElementId], 'highlight-selected');
    }
  }

  private fit(): void {
    if (this.destroyed || !this.viewer) {
      return;
    }

    try {
      const canvas = this.viewer.get('canvas');
      canvas?.resized?.();
      canvas?.zoom?.('fit-viewport', 'auto');
    } catch {
      // keep the runtime desk usable even if fit fails
    }
  }
}
